import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import axios from "axios";
import { TbTrashX } from "react-icons/tb";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ConfirmDialog from "./ConfirmDialog";
import PrintRequestPage from "./PrintRequestPage";
import { fetchFormNos } from "../utils/formNos";
import { FORM_KEYS } from "../config/formIds";

const thCls =
  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
const tdCls =
  "px-4 py-2.5 text-xs text-gray-700 border-r last:border-r-0 whitespace-nowrap";

function RequestList() {
  const [record, setRecord] = useState([]);
  const { user } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [tempArr, setTempArr] = useState([]);
  const [printData, setPrintData] = useState(null);
  const [formNos, setFormNos] = useState({});

  useEffect(() => {
    fetchFormNos().then(setFormNos);
    getAllPending();
  }, []);

  const getAllPending = async () => {
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/store/transaction/pending/",
      );
      const sorted = res.data.result.sort(
        (a, b) => new Date(b.transaction_time) - new Date(a.transaction_time),
      );
      setRecord(sorted);
    } catch {
      toast.error("Failed to fetch records");
    }
  };

  const cancelRequest = async (data) => {
    setIsLoading(true);
    try {
      await axios.put(
        "https://eps.elkomprintama.com/api/store/stocks/cancel_request",
        {
          groupId: data.transaction_group_id,
          approvedBy: user.username,
          transaction_id: data.transaction_id,
        },
      );
      toast.success("Request cancelled.");
      getAllPending();
    } catch (err) {
      toast.error(
        "Failed to cancel: " + (err?.response?.data?.msg || err.message),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const groupedData = record.reduce((acc, curr) => {
    const g = curr.transaction_group_id || "UNGROUPED";
    if (!acc[g]) acc[g] = [];
    acc[g].push(curr);
    return acc;
  }, {});

  const sortedGroups = Object.entries(groupedData).sort(([, a], [, b]) => {
    const latest = (arr) =>
      Math.max(...arr.map((x) => new Date(x.transaction_time)));
    return latest(b) - latest(a);
  });

  const isVoid = (data) => data.transaction_remark?.includes("[VOID]");

  const typeColor = (type) => {
    if (type === "Stock In") return "bg-green-100 text-green-700";
    if (type === "Pending") return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Loading…" />}

      {/* Header */}
      <div className="w-full rounded-lg bg-rose-800 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Request List
        </label>
      </div>

      {/* Table card */}
      <div className="flex-1 min-h-0 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="flex-1 min-h-0 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-gray-700 text-white text-xs uppercase">
              <tr>
                <th className={thCls}>No</th>
                <th className={thCls}>Product Name</th>
                <th className={thCls}>Code</th>
                <th className={thCls}>Group</th>
                <th className={thCls}>QTY</th>
                <th className={thCls}>Type</th>
                <th className={thCls}>Customer</th>
                <th className={thCls}>Remark</th>
                <th className={thCls}>Input By</th>
                <th className={thCls}>Input Time</th>
                <th className={thCls}></th>
              </tr>
            </thead>
            <tbody>
              {sortedGroups.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="py-16 text-center text-gray-400 italic text-sm"
                  >
                    No pending requests
                  </td>
                </tr>
              ) : (
                sortedGroups.map(([groupId, items]) => {
                  const sortedItems = [...items].sort(
                    (a, b) =>
                      new Date(b.transaction_time) -
                      new Date(a.transaction_time),
                  );
                  return (
                    <React.Fragment key={groupId}>
                      {/* Group header row */}
                      <tr className="bg-gray-600 border-t-4 border-gray-300">
                        <td
                          colSpan={11}
                          className="px-4 py-2 text-xs font-bold text-white uppercase tracking-widest"
                        >
                          Form #{sortedItems[0]?.form_id ?? "—"} ·{" "}
                          {sortedItems.length} item
                          {sortedItems.length !== 1 ? "s" : ""}
                        </td>
                      </tr>
                      {sortedItems.map((data, i) => (
                        <tr
                          key={`row-${groupId}-${i}`}
                          className={`border-b hover:bg-rose-50 ${
                            isVoid(data)
                              ? "bg-red-100 line-through text-red-400"
                              : i % 2 === 0
                                ? "bg-gray-50"
                                : "bg-white"
                          }`}
                        >
                          <td className={tdCls}>{data.form_id}</td>
                          <td className={`${tdCls} font-medium text-gray-800`}>
                            {data.product_name}
                          </td>
                          <td className={tdCls}>{data.product_code}</td>
                          <td className={tdCls}>{data.group_name}</td>
                          <td className={`${tdCls} text-center font-bold`}>
                            {data.stock_out}
                          </td>
                          <td className="px-4 py-2.5 border-r whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${typeColor(data.transaction_type)}`}
                            >
                              {data.transaction_type}
                            </span>
                          </td>
                          <td className={tdCls}>
                            {data.transaction_for || "—"}
                          </td>
                          <td className={tdCls}>
                            {data.transaction_remark || "—"}
                          </td>
                          <td className={tdCls}>{data.transaction_by}</td>
                          <td className={tdCls}>
                            {format(
                              new Date(data.transaction_time),
                              "dd-MM-yyyy HH:mm:ss",
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-center whitespace-nowrap">
                            <button
                              className="text-blue-400 hover:text-blue-600 mr-2 transition"
                              onClick={(e) => {
                                e.preventDefault();
                                const groupItems = record.filter(
                                  (item) =>
                                    item.transaction_group_id ===
                                    data.transaction_group_id,
                                );
                                setPrintData({
                                  formId: data.form_id,
                                  groupId: data.transaction_group_id,
                                  items: groupItems,
                                });
                              }}
                            >
                              🖨️
                            </button>
                            <button
                              className="text-red-400 hover:text-red-600 transition"
                              onClick={(e) => {
                                e.preventDefault();
                                setTempArr(data);
                                setShowConfirm(true);
                              }}
                            >
                              <TbTrashX size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {printData && (
        <PrintRequestPage
          formId={printData.formId}
          groupId={printData.groupId}
          items={printData.items}
          onDone={() => setPrintData(null)}
          useNow={false}
          formNumber={formNos[FORM_KEYS.MATERIAL_REQUEST]}
        />
      )}

      {showConfirm && (
        <ConfirmDialog
          message="Cancel this material request? This cannot be undone."
          onConfirm={() => {
            setShowConfirm(false);
            cancelRequest(tempArr);
          }}
          onCancel={() => {
            setShowConfirm(false);
            setTempArr([]);
          }}
        />
      )}
    </div>
  );
}

export default RequestList;
